import { useEffect, useRef, useState, RefObject } from 'react'

interface Posicao {
  left: number
  width: number
  top?: number
  bottom?: number
}

// Corrige o dropdown de busca (".nat-lista") ficando espremido/cortado quando o campo
// está perto do fim de um contêiner com scroll (ex: modal de item da nota) — antes a
// lista era posicionada com `position: absolute` dentro do próprio formulário, e o
// `overflow: auto` do modal cortava ela. Aqui a lista é jogada num portal (fora do
// modal, direto no body) e posicionada via `position: fixed`, calculada a partir do
// campo (`gatilhoRef`); abre pra cima quando não há espaço suficiente embaixo.
export function useListaFlutuante(gatilhoRef: RefObject<HTMLElement>, aberta: boolean, fechar: () => void, largura?: number) {
  const [posicao, setPosicao] = useState<Posicao | null>(null)
  const listaRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!aberta) { setPosicao(null); return }
    const rect = gatilhoRef.current?.getBoundingClientRect()
    if (!rect) return
    const larguraFinal = largura ?? rect.width
    const alturaEstimada = 420
    const espacoAbaixo = window.innerHeight - rect.bottom
    const abrirParaCima = espacoAbaixo < alturaEstimada && rect.top > espacoAbaixo
    setPosicao(
      abrirParaCima
        ? { bottom: window.innerHeight - rect.top + 4, left: rect.left, width: larguraFinal }
        : { top: rect.bottom + 4, left: rect.left, width: larguraFinal }
    )
  }, [aberta, largura, gatilhoRef])

  useEffect(() => {
    if (!aberta) return
    function aoClicarFora(e: MouseEvent) {
      const alvo = e.target as Node
      if (gatilhoRef.current?.contains(alvo)) return
      if (listaRef.current?.contains(alvo)) return
      fechar()
    }
    // Rolar a própria lista de resultados (a barrinha de rolagem dela) também dispara
    // "scroll" em fase de captura — não pode fechar nesse caso, só quando o scroll é
    // de algo por trás da lista (a página ou o modal).
    function aoRolar(e: Event) {
      if (listaRef.current?.contains(e.target as Node)) return
      fechar()
    }
    function aoRedimensionar() { fechar() }
    document.addEventListener('mousedown', aoClicarFora)
    window.addEventListener('scroll', aoRolar, true)
    window.addEventListener('resize', aoRedimensionar)
    return () => {
      document.removeEventListener('mousedown', aoClicarFora)
      window.removeEventListener('scroll', aoRolar, true)
      window.removeEventListener('resize', aoRedimensionar)
    }
  }, [aberta, fechar, gatilhoRef])

  return { posicao, listaRef }
}
